import React, { useEffect, useState, useCallback } from "react";
import {
  Paper, Table, TableHead, TableBody, TableRow, TableCell, TextField, MenuItem,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Chip, makeStyles
} from "@material-ui/core";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import { toast } from "react-toastify";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), margin: theme.spacing(2) },
  filters: { display: "flex", gap: theme.spacing(2), flexWrap: "wrap", marginBottom: theme.spacing(2) },
  field: { minWidth: 160 },
  pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: theme.spacing(2) }
}));

const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const KIND_COLOR = { income: "primary", expense: "secondary", transfer: "default" };
const KIND_LABEL = { income: "Entrada", expense: "Saída", transfer: "Transferência" };

const Transactions = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(30);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ accountId: "", kind: "", dateFrom: "", dateTo: "" });
  const [transferDialog, setTransferDialog] = useState({
    open: false, accountId: "", transferToAccountId: "", amount: "", description: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const params = { pageNumber: page, pageSize };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/finance/transactions", { params });
      setRows(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      toast.error("Erro ao carregar transações");
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    api.get("/finance/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const doTransfer = async () => {
    try {
      await api.post("/finance/transactions/transfer", {
        accountId: Number(transferDialog.accountId),
        transferToAccountId: Number(transferDialog.transferToAccountId),
        amount: Number(transferDialog.amount),
        description: transferDialog.description || null
      });
      toast.success("Transferência registrada");
      setTransferDialog({ open: false, accountId: "", transferToAccountId: "", amount: "", description: "" });
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro na transferência");
    }
  };

  const reverse = async (tx) => {
    if (!(await confirm({ title: `Estornar a transação #${tx.id}?`, message: "O saldo da conta financeira é ajustado e o lançamento de origem volta a ficar em aberto." }))) return;
    try {
      await api.post(`/finance/transactions/${tx.id}/reverse`);
      toast.success("Estornada");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erro ao estornar");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Paper className={classes.root}>
      {confirmDialog}
      <h2>Movimentações Financeiras</h2>
      <div className={classes.filters}>
        <TextField className={classes.field} select size="small" variant="outlined" label="Conta"
          value={filters.accountId}
          onChange={(e) => { setPage(1); setFilters({ ...filters, accountId: e.target.value }); }}>
          <MenuItem value="">Todas</MenuItem>
          {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
        </TextField>
        <TextField className={classes.field} select size="small" variant="outlined" label="Tipo"
          value={filters.kind}
          onChange={(e) => { setPage(1); setFilters({ ...filters, kind: e.target.value }); }}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="income">Entrada</MenuItem>
          <MenuItem value="expense">Saída</MenuItem>
          <MenuItem value="transfer">Transferência</MenuItem>
        </TextField>
        <TextField className={classes.field} size="small" variant="outlined" type="date" label="De"
          InputLabelProps={{ shrink: true }} value={filters.dateFrom}
          onChange={(e) => { setPage(1); setFilters({ ...filters, dateFrom: e.target.value }); }} />
        <TextField className={classes.field} size="small" variant="outlined" type="date" label="Até"
          InputLabelProps={{ shrink: true }} value={filters.dateTo}
          onChange={(e) => { setPage(1); setFilters({ ...filters, dateTo: e.target.value }); }} />
        <Button color="primary" variant="contained" startIcon={<SwapHorizIcon />}
          onClick={() => setTransferDialog({ ...transferDialog, open: true })}>Transferir</Button>
      </div>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Conta</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((tx) => (
            <TableRow key={tx.id} style={tx.reversedTransactionId ? { opacity: 0.5 } : null}>
              <TableCell>{new Date(tx.date).toLocaleString("pt-BR")}</TableCell>
              <TableCell><Chip size="small" label={KIND_LABEL[tx.kind] || tx.kind} color={KIND_COLOR[tx.kind] || "default"} /></TableCell>
              <TableCell>{tx.account?.name}{tx.kind === "transfer" && tx.transferToAccount ? ` → ${tx.transferToAccount.name}` : ""}</TableCell>
              <TableCell>{tx.description}</TableCell>
              <TableCell>{tx.category?.name || "-"}</TableCell>
              <TableCell align="right">{fmt(tx.amount)}</TableCell>
              <TableCell align="center">
                {!tx.reversedTransactionId && (
                  <Button size="small" onClick={() => reverse(tx)}>Estornar</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7} align="center">Sem movimentos.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <div className={classes.pagination}>
        <span>{total} transação(ões)</span>
        <div>
          <Button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span style={{ margin: "0 8px" }}>{page} / {totalPages}</span>
          <Button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
        </div>
      </div>

      <Dialog open={transferDialog.open} onClose={() => setTransferDialog({ ...transferDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Transferência entre contas</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" variant="outlined" label="Origem"
                value={transferDialog.accountId}
                onChange={(e) => setTransferDialog({ ...transferDialog, accountId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name} — {fmt(a.currentBalance)}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" variant="outlined" label="Destino"
                value={transferDialog.transferToAccountId}
                onChange={(e) => setTransferDialog({ ...transferDialog, transferToAccountId: e.target.value })}>
                <MenuItem value="">—</MenuItem>
                {accounts.filter((a) => a.id !== Number(transferDialog.accountId))
                  .map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" size="small" variant="outlined" label="Valor"
                value={transferDialog.amount}
                onChange={(e) => setTransferDialog({ ...transferDialog, amount: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" variant="outlined" label="Descrição"
                value={transferDialog.description}
                onChange={(e) => setTransferDialog({ ...transferDialog, description: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog({ ...transferDialog, open: false })}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={!transferDialog.accountId || !transferDialog.transferToAccountId || !transferDialog.amount}
            onClick={doTransfer}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default Transactions;
